/**
 * FirewallOptionsCard Component
 *
 * Displays and allows editing of VM firewall options.
 */

import {
  SettingsIcon,
  ShieldIcon,
  ShieldOffIcon,
  RefreshCwIcon,
  AlertTriangleIcon,
  InfoIcon,
  Loader2Icon,
} from 'lucide-react'
import { useState } from 'react'

import type { FirewallOptions, FirewallOptionsUpdate } from '@nebula/shared/types/firewall'

interface FirewallOptionsCardProps {
  options: FirewallOptions | null
  isLoading?: boolean
  onToggleFirewall: (enabled: boolean) => Promise<void>
  onUpdateOptions: (options: FirewallOptionsUpdate) => Promise<void>
  onSync: () => Promise<void>
}

const FirewallOptionsCard = ({
  options,
  isLoading = false,
  onToggleFirewall,
  onUpdateOptions,
  onSync,
}: FirewallOptionsCardProps) => {
  const [isToggling, setIsToggling] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [showAdvanced, setShowAdvanced] = useState(false)

  /**
   * Três estados, não dois. `enabled` chega `null` quando o backend NÃO conseguiu ler o
   * Proxmox (agent fora do ar, VM ainda sem vínculo). Renderizar isso como "Inativo" é
   * afirmar o que não se apurou — e ainda oferece um "Ativar" que vai falhar.
   */
  const ligado = options?.enabled === true
  const desconhecido = options != null && options.enabled == null

  const handleToggle = async () => {
    if (!options || desconhecido) return
    setIsToggling(true)
    try {
      await onToggleFirewall(!ligado)
    } finally {
      setIsToggling(false)
    }
  }

  const handleSync = async () => {
    setIsSyncing(true)
    try {
      await onSync()
    } finally {
      setIsSyncing(false)
    }
  }

  const handleOptionChange = async (key: keyof FirewallOptionsUpdate, value: boolean | string) => {
    await onUpdateOptions({ [key]: value })
  }

  if (isLoading) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-1/3 rounded bg-gray-200"></div>
          <div className="h-4 w-2/3 rounded bg-gray-200"></div>
          <div className="h-10 rounded bg-gray-200"></div>
        </div>
      </div>
    )
  }

  if (!options) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <div className="text-center text-gray-500">
          <ShieldOffIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          <p className="text-sm">Selecione uma VM para ver as opções de firewall</p>
        </div>
      </div>
    )
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
        <div className="flex items-center space-x-3">
          <div className={`rounded-lg p-2 ${ligado ? 'bg-green-100' : 'bg-gray-100'}`}>
            {ligado ? (
              <ShieldIcon className="h-5 w-5 text-green-600" />
            ) : (
              <ShieldOffIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Status do Firewall</h3>
            <p className="text-sm text-gray-500">
              {desconhecido
                ? 'Não foi possível consultar o estado do firewall agora'
                : ligado
                  ? 'Firewall ativo e protegendo a VM'
                  : 'Firewall desativado'}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleSync}
            disabled={isSyncing}
            className="rounded-lg p-2 text-gray-400 hover:bg-purple-50 hover:text-purple-600 disabled:opacity-50"
            title="Atualizar estado do firewall"
          >
            <RefreshCwIcon className={`h-5 w-5 ${isSyncing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={handleToggle}
            // Desconhecido desabilita: sem saber o estado atual, não há direção de
            // alternância que signifique alguma coisa.
            disabled={isToggling || desconhecido}
            title={desconhecido ? 'Estado do firewall desconhecido' : undefined}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              ligado
                ? 'bg-red-50 text-red-600 hover:bg-red-100'
                : 'bg-green-50 text-green-600 hover:bg-green-100'
            } disabled:opacity-50`}
          >
            {isToggling ? (
              <Loader2Icon className="h-4 w-4 animate-spin" />
            ) : desconhecido ? (
              'Indisponível'
            ) : ligado ? (
              'Desativar'
            ) : (
              'Ativar'
            )}
          </button>
        </div>
      </div>

      {/* Quick Status */}
      <div className="grid grid-cols-2 gap-4 px-6 py-4">
        <div className="flex items-center space-x-3 rounded-lg bg-gray-50 p-3">
          <div
            className={`h-3 w-3 rounded-full ${
              desconhecido ? 'bg-amber-400' : ligado ? 'bg-green-500' : 'bg-gray-400'
            }`}
          />
          <div>
            <div className="text-xs text-gray-500">Status</div>
            <div
              className={`text-sm font-medium ${
                desconhecido ? 'text-amber-700' : ligado ? 'text-green-700' : 'text-gray-600'
              }`}
            >
              {desconhecido ? 'Desconhecido' : ligado ? 'Ativo' : 'Inativo'}
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-3 rounded-lg bg-gray-50 p-3">
          <div
            className={`h-3 w-3 rounded-full ${
              options.policy_in === 'DROP' ? 'bg-red-500' : 'bg-green-500'
            }`}
          />
          <div>
            <div className="text-xs text-gray-500">Política Entrada</div>
            <div
              className={`text-sm font-medium ${
                options.policy_in === 'DROP' ? 'text-red-700' : 'text-green-700'
              }`}
            >
              {options.policy_in === 'DROP' ? 'Bloqueio Padrão' : 'Aceito Padrão'}
            </div>
          </div>
        </div>
      </div>

      {/* Warning if firewall is disabled — só quando se SABE que está desligado. */}
      {!ligado && !desconhecido && (
        <div className="mx-6 mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-start space-x-2">
            <AlertTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" />
            <div className="text-sm text-amber-700">
              <strong>Atenção:</strong> O firewall está desativado. Sua VM está exposta a todo
              tráfego de rede. Recomendamos ativar o firewall para maior segurança.
            </div>
          </div>
        </div>
      )}

      {/* Advanced Options Toggle */}
      <div className="px-6 pb-4">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center space-x-1 text-sm text-purple-600 hover:text-purple-700"
        >
          <SettingsIcon className="h-4 w-4" />
          <span>{showAdvanced ? 'Ocultar opções avançadas' : 'Mostrar opções avançadas'}</span>
        </button>
      </div>

      {/* Advanced Options */}
      {showAdvanced && (
        <div className="space-y-4 border-t border-gray-200 px-6 pt-4 pb-6">
          <div className="mb-4 flex items-start space-x-2 rounded-lg bg-blue-50 p-3">
            <InfoIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
            <p className="text-xs text-blue-700">
              Estas opções controlam o comportamento do firewall desta VM. Altere com cuidado para
              evitar perda de conectividade.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* DHCP */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <div className="text-sm font-medium text-gray-700">DHCP</div>
                <div className="text-xs text-gray-500">Permitir tráfego DHCP</div>
              </div>
              <button
                onClick={() => handleOptionChange('dhcp', !options.dhcp)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  options.dhcp ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    options.dhcp ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* NDP */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <div className="text-sm font-medium text-gray-700">NDP (IPv6)</div>
                <div className="text-xs text-gray-500">Permitir Neighbor Discovery</div>
              </div>
              <button
                onClick={() => handleOptionChange('ndp', !options.ndp)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  options.ndp ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    options.ndp ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* MAC Filter */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <div className="text-sm font-medium text-gray-700">MAC Filter</div>
                <div className="text-xs text-gray-500">Filtrar por endereço MAC</div>
              </div>
              <button
                onClick={() => handleOptionChange('macfilter', !options.macfilter)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  options.macfilter ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    options.macfilter ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* IP Filter */}
            <div className="flex items-center justify-between rounded-lg bg-gray-50 p-3">
              <div>
                <div className="text-sm font-medium text-gray-700">IP Filter</div>
                <div className="text-xs text-gray-500">Filtrar por endereço IP</div>
              </div>
              <button
                onClick={() => handleOptionChange('ipfilter', !options.ipfilter)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  options.ipfilter ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    options.ipfilter ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Policies */}
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="mb-2 text-sm font-medium text-gray-700">Política de Entrada</div>
              <select
                value={options.policy_in}
                onChange={(e) => handleOptionChange('policy_in', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="DROP">DROP (Bloquear)</option>
                <option value="ACCEPT">ACCEPT (Aceitar)</option>
                <option value="REJECT">REJECT (Rejeitar)</option>
              </select>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="mb-2 text-sm font-medium text-gray-700">Política de Saída</div>
              <select
                value={options.policy_out}
                onChange={(e) => handleOptionChange('policy_out', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              >
                <option value="ACCEPT">ACCEPT (Aceitar)</option>
                <option value="DROP">DROP (Bloquear)</option>
                <option value="REJECT">REJECT (Rejeitar)</option>
              </select>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FirewallOptionsCard
